import { Game, emptyGame, emptyScene } from '../types/game';
import { SceneObject, Behavior } from '../types';

export interface GameTemplate {
  id: string;
  title: string;
  description: string;
  build: (projectId: string) => Game;
}

const FLOOR_ROT = { x: -Math.PI / 2, y: 0, z: 0 };

let n = 0;
function mk(
  type: SceneObject['type'],
  name: string,
  pos: [number, number, number],
  color: string,
  behaviors: Behavior[] = [],
  scale: [number, number, number] = [1, 1, 1],
  rotation = { x: 0, y: 0, z: 0 }
): SceneObject {
  n += 1;
  return {
    uuid: `tpl_${name.replace(/\s+/g, '_')}_${n}`,
    name,
    type,
    position: { x: pos[0], y: pos[1], z: pos[2] },
    rotation,
    scale: { x: scale[0], y: scale[1], z: scale[2] },
    color,
    behaviors,
  };
}

// --- building blocks -------------------------------------------------------
const player = (pos: [number, number, number]): SceneObject =>
  mk('cube', 'Player', pos, '#2B8CFF', [{ type: 'player', params: { speed: 6 } }]);
const coin = (name: string, pos: [number, number, number]): SceneObject =>
  mk('sphere', name, pos, '#F4B942', [{ type: 'collectible', params: { points: 1 } }], [0.5, 0.5, 0.5]);
const gem = (name: string, pos: [number, number, number]): SceneObject =>
  mk('sphere', name, pos, '#A855F7', [{ type: 'collectible', params: { points: 5 } }], [0.55, 0.55, 0.55]);
const goal = (pos: [number, number, number]): SceneObject =>
  mk('cube', 'Goal', pos, '#22C55E', [{ type: 'goal', params: { requireAllCollectibles: true } }], [1, 1.4, 1]);
const wall = (name: string, pos: [number, number, number], scale: [number, number, number], color = '#5B6470'): SceneObject =>
  mk('cube', name, pos, color, [{ type: 'obstacle', params: {} }], scale);
const hazard = (name: string, pos: [number, number, number]): SceneObject =>
  mk('cube', name, pos, '#EF4444', [{ type: 'hazard', params: {} }], [0.9, 0.5, 0.9]);
const floor = (color: string, size = 22): SceneObject =>
  mk('plane', 'Ground', [0, 0, 0], color, [], [size, size, 1], FLOOR_ROT);

// decoration (no behavior — pure scenery)
const tree = (name: string, x: number, z: number): SceneObject[] => [
  mk('cube', `${name} trunk`, [x, 0.6, z], '#6B4A2B', [], [0.3, 1.2, 0.3]),
  mk('sphere', `${name} leaves`, [x, 1.7, z], '#43C46A', [], [1.1, 1.2, 1.1]),
];
const bush = (name: string, x: number, z: number): SceneObject =>
  mk('sphere', name, [x, 0.4, z], '#4FB36A', [], [0.8, 0.7, 0.8]);
const rock = (name: string, x: number, z: number): SceneObject =>
  mk('sphere', name, [x, 0.3, z], '#737B89', [], [0.9, 0.6, 0.9]);

function game(projectId: string, title: string, description: string, objects: SceneObject[]): Game {
  n = 0;
  return emptyGame({
    id: projectId,
    title,
    description,
    scenes: [emptyScene({ name: 'Main Scene', objects })],
  });
}

export const GAME_TEMPLATES: GameTemplate[] = [
  {
    id: 'coin-collector',
    title: 'Coin Quest',
    description: 'A leafy little world — grab all eight coins, then reach the goal.',
    build: (pid) =>
      game(pid, 'Coin Quest', 'Collect every coin, then reach the goal.', [
        floor('#2E7D52'),
        player([0, 0.5, 8]),
        // coins in a sweeping path
        coin('Coin 1', [-4, 0.5, 5]),
        coin('Coin 2', [4, 0.5, 5]),
        coin('Coin 3', [-5, 0.5, 1]),
        coin('Coin 4', [5, 0.5, 1]),
        coin('Coin 5', [-2, 0.5, -2]),
        coin('Coin 6', [2, 0.5, -2]),
        coin('Coin 7', [0, 0.5, 3]),
        gem('Gem', [0, 0.5, -5]),
        // light obstacles to weave around
        ...[
          [3, 3] as [number, number],
          [-3, -1] as [number, number],
        ].map(([x, z], i) => wall(`Crate ${i + 1}`, [x, 0.5, z], [1, 1, 1], '#8A6D3B')),
        // scenery
        ...tree('Tree 1', -8, 7),
        ...tree('Tree 2', 8, 7),
        ...tree('Tree 3', -8, -6),
        ...tree('Tree 4', 8, -6),
        bush('Bush 1', -6, 3),
        bush('Bush 2', 6, -3),
        rock('Rock 1', 5, 6),
        rock('Rock 2', -5, -4),
        goal([0, 0.7, -8]),
      ]),
  },
  {
    id: 'maze-runner',
    title: 'Maze Runner',
    description: 'Wind through stone corridors, pocket the coins, and find the exit.',
    build: (pid) =>
      game(pid, 'Maze Runner', 'Navigate the maze to the exit.', [
        floor('#2B2B3A', 24),
        player([-8, 0.5, 8]),
        // outer + inner walls forming corridors
        wall('Wall N', [0, 0.5, -9], [20, 1.2, 1]),
        wall('Wall S', [0, 0.5, 9], [20, 1.2, 1]),
        wall('Wall E', [10, 0.5, 0], [1, 1.2, 19]),
        wall('Wall W', [-10, 0.5, 0], [1, 1.2, 19]),
        wall('Maze 1', [-3, 0.5, 5], [1, 1.2, 9]),
        wall('Maze 2', [3, 0.5, -5], [1, 1.2, 9]),
        wall('Maze 3', [-3, 0.5, -7], [6, 1.2, 1]),
        wall('Maze 4', [4, 0.5, 3], [6, 1.2, 1]),
        coin('Coin 1', [-8, 0.5, -6]),
        coin('Coin 2', [0, 0.5, 7]),
        coin('Coin 3', [7, 0.5, -7]),
        gem('Gem', [8, 0.5, 7]),
        goal([8, 0.7, -8]),
      ]),
  },
  {
    id: 'avoid-the-hazards',
    title: 'Danger Zone',
    description: 'Thread between the red hazards to collect the loot and escape.',
    build: (pid) =>
      game(pid, 'Danger Zone', 'Touch a red block and you go back to start.', [
        floor('#15151F', 22),
        player([0, 0.5, 9]),
        // hazard field
        hazard('Hazard 1', [-3, 0.5, 5]),
        hazard('Hazard 2', [3, 0.5, 5]),
        hazard('Hazard 3', [0, 0.5, 2]),
        hazard('Hazard 4', [-4, 0.5, -1]),
        hazard('Hazard 5', [4, 0.5, -1]),
        hazard('Hazard 6', [-2, 0.5, -4]),
        hazard('Hazard 7', [2, 0.5, -4]),
        coin('Coin 1', [0, 0.5, 5]),
        coin('Coin 2', [-4, 0.5, 2]),
        coin('Coin 3', [4, 0.5, 2]),
        coin('Coin 4', [0, 0.5, -2]),
        gem('Gem', [0, 0.5, -6]),
        // border walls
        wall('Edge W', [-9, 0.5, 0], [1, 1, 20]),
        wall('Edge E', [9, 0.5, 0], [1, 1, 20]),
        goal([0, 0.7, -9]),
      ]),
  },
  {
    id: 'treasure-arena',
    title: 'Treasure Arena',
    description: 'A walled arena packed with gems — sweep it clean and reach the goal.',
    build: (pid) =>
      game(pid, 'Treasure Arena', 'Clear the arena, then reach the goal.', [
        floor('#23304A', 20),
        player([0, 0.5, 7]),
        // arena walls
        wall('Arena N', [0, 0.5, -8], [16, 1.2, 1], '#4A5160'),
        wall('Arena S', [0, 0.5, 8], [16, 1.2, 1], '#4A5160'),
        wall('Arena E', [8, 0.5, 0], [1, 1.2, 17], '#4A5160'),
        wall('Arena W', [-8, 0.5, 0], [1, 1.2, 17], '#4A5160'),
        // treasure
        gem('Gem 1', [-5, 0.5, 4]),
        gem('Gem 2', [5, 0.5, 4]),
        gem('Gem 3', [-5, 0.5, -4]),
        gem('Gem 4', [5, 0.5, -4]),
        coin('Coin 1', [0, 0.5, 4]),
        coin('Coin 2', [0, 0.5, -4]),
        // central hazards
        hazard('Hazard 1', [-2, 0.5, 0]),
        hazard('Hazard 2', [2, 0.5, 0]),
        goal([0, 0.7, -6]),
      ]),
  },
];

export function getTemplate(id: string): GameTemplate | undefined {
  return GAME_TEMPLATES.find((t) => t.id === id);
}
